import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useResources from '@/data/hook/resource/useResources';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import { resourceFieldsToDTO } from '@/utils/resource/resourceFieldsToDTO';
import { ServiceUploadingOptions } from '@/utils/shared/uploadProgress';

import ResourceCard from './ResourceCard';
import ResourceFormList from './ResourceFormList';
import { ResourceFormFields } from './ResourceInlineForm';

type ResourcesTabContentProps = {
  lessonId: number;
  isLimitedEdition?: boolean;
};

export default function ResourcesTabContent({
  lessonId,
  isLimitedEdition = false,
}: ResourcesTabContentProps) {
  const {
    resources,
    isLoading,
    createResource,
    isCreating,
    updateResource,
    isUpdating,
    deleteResource,
    reorderResources,
    isReordering,
  } = useResources({ lessonId });

  const { optimistic: optimisticResources, handleDragEnd } =
    useOptimisticReorder({
      items: resources,
      isReordering,
      onReorder: reorderResources,
    });

  const handleCreateSubmit = (
    data: ResourceFormFields,
    options?: ServiceUploadingOptions,
  ) =>
    createResource({
      body: resourceFieldsToDTO(data),
      onUploadProgress: options?.onUploadProgress,
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <DragDropList
        groupId={`resources-list-${lessonId}`}
        onDragEnd={handleDragEnd}
        disabled={isReordering || isLimitedEdition}
        className="gap-0"
      >
        {optimisticResources.map((resource, index) => (
          <ResourceCard
            key={resource.id}
            resource={resource}
            ordinal={index + 1}
            lessonId={lessonId}
            onUpdate={updateResource}
            onDelete={deleteResource}
            isUpdating={isUpdating}
            isDraggable
            id={String(resource.id)}
            index={index}
            className="mb-6"
            isLimitedEdition={isLimitedEdition}
          />
        ))}
      </DragDropList>

      <ResourceFormList
        onSubmit={handleCreateSubmit}
        isLoading={isCreating}
        isLimitedEdition={isLimitedEdition}
      />
    </div>
  );
}
